import * as React from 'react'

import { CameraIcon, PaperclipIcon } from 'lucide-react'

import { IAttachment } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FilePicker } from '~/client/src/shared/components/FilePicker'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { CloseIcon } from '../icons/CloseIcon'
import StruxhubInputLabel from '../StruxhubInputs/components/StruxhubInputLabel'
import { getFileKind, getFileTypeText } from './fileKind'
import FileTypeIcon from './FileTypeIcon'

interface FilesInputProps {
  /** External value — array of attachments. */
  value: IAttachment[]
  /** Called with the next array whenever an attachment is added or removed. */
  onChange: (next: IAttachment[]) => void
  /**
   * Uploads a single picked file and resolves to the attachment metadata to
   * be appended to `value`. Throw to surface a user-visible upload error.
   */
  uploadFn: (file: File) => Promise<IAttachment>

  label?: string
  isRequired?: boolean
  isReadOnly?: boolean
  isDisabled?: boolean

  /**
   * MIME types and/or extensions accepted by the file picker AND validated
   * client-side after pick (e.g. ['.pdf', 'image/*']).
   */
  acceptedFileTypes?: string[]
  /** Hard cap on the number of attachments. */
  maxFiles?: number
  /** Per-file size cap in megabytes. */
  maxFileSizeMb?: number

  /** Label for the primary upload button. Defaults to localized "Attach file". */
  uploadButtonLabel?: string
  /** Label for the camera button. Defaults to "Camera". */
  cameraButtonLabel?: string

  /**
   * When true (and `isMobileEnv` is true), shows a second button that opens
   * the device camera (image/*, capture=environment).
   */
  enableCameraOnMobile?: boolean
  /** Pass-through from MobX `inject('isMobileEnv')` if the consumer wants the camera button. */
  isMobileEnv?: boolean

  /** Hide the inline error/helper text under the input. */
  hideHelperText?: boolean
  /** External error message (e.g. from a form library) shown under the input. */
  errorMessage?: string

  /** Called when clicking an attachment preview. Receives all attachments and the clicked index. */
  onPreview?: (attachments: IAttachment[], index: number) => void
}

export default function FilesInput({
  value,
  onChange,
  uploadFn,
  label,
  isRequired,
  isReadOnly,
  isDisabled,
  acceptedFileTypes,
  maxFiles,
  maxFileSizeMb,
  uploadButtonLabel,
  cameraButtonLabel,
  enableCameraOnMobile,
  isMobileEnv,
  hideHelperText,
  errorMessage,
  onPreview,
}: FilesInputProps) {
  const [isUploading, setIsUploading] = React.useState(false)
  const [uploadError, setUploadError] = React.useState<string | null>(null)

  const attachments = value || []
  const maxReached = maxFiles != null && attachments.length >= maxFiles

  const acceptPattern = React.useMemo(() => {
    if (!acceptedFileTypes?.length) return undefined
    return acceptedFileTypes.join(',')
  }, [acceptedFileTypes])

  const handleFileSelect = async ([file]: File[]) => {
    if (maxReached) {
      setUploadError(Localization.translator.maxFilesReached)
      return
    }

    if (maxFileSizeMb && file.size > maxFileSizeMb * 1024 * 1024) {
      setUploadError(Localization.translator.fileTooLarge(maxFileSizeMb))
      return
    }

    if (acceptedFileTypes?.length > 0) {
      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`
      const mimeType = file.type
      const isAllowed = acceptedFileTypes.some(
        type =>
          type === extension ||
          type === mimeType ||
          (type.endsWith('/*') && mimeType.startsWith(type.replace('/*', '/'))),
      )
      if (!isAllowed) {
        setUploadError(Localization.translator.fileTypeNotAllowed)
        return
      }
    }

    setUploadError(null)
    setIsUploading(true)
    try {
      const attachment = await uploadFn(file)
      onChange([...attachments, attachment])
    } catch (err) {
      setUploadError(
        err instanceof Error
          ? err.message
          : Localization.translator.uploadFailed,
      )
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    onChange(attachments.filter((_, i) => i !== index))
  }

  const openAttachment = (index: number) => {
    const attachment = attachments[index]
    if (!attachment) return
    if (onPreview) {
      onPreview(attachments, index)
      return
    }
    if (!attachment.url) return
    window.open(attachment.url, '_blank', 'noopener,noreferrer')
  }

  const displayError = errorMessage || uploadError
  const showCamera = !!enableCameraOnMobile && !!isMobileEnv

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:mt-2">
      {label && (
        <StruxhubInputLabel
          label={label}
          value={attachments.length > 0 || displayError ? '1' : ''}
          isRequired={!!isRequired}
          isValid={!displayError}
        />
      )}

      {!isReadOnly && !isDisabled && !maxReached && (
        <div className="tw:flex tw:gap-2">
          <FilePicker
            accept={acceptPattern}
            disabled={isUploading}
            onPick={handleFileSelect}
            aria-invalid={!!displayError}
            render={<Button variant="outline" className="tw:flex-1" />}
          >
            {isUploading ? (
              <span data-icon="inline-start">
                <Spinner size="xs" />
              </span>
            ) : (
              <PaperclipIcon data-icon="inline-start" />
            )}
            {isUploading
              ? Localization.translator.uploading
              : uploadButtonLabel || Localization.translator.attachFile}
          </FilePicker>
          {showCamera && (
            <FilePicker
              accept="image/*"
              capture="environment"
              disabled={isUploading}
              onPick={handleFileSelect}
              aria-invalid={!!displayError}
              render={<Button variant="outline" className="tw:flex-1" />}
            >
              <CameraIcon data-icon="inline-start" />
              {cameraButtonLabel || Localization.translator.camera}
            </FilePicker>
          )}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="tw:flex tw:flex-col tw:gap-2">
          {attachments.map((attachment, index) => {
            const kind = getFileKind(attachment.fileName)
            const typeText = getFileTypeText(kind)
            return (
              <div
                key={`${attachment.fileName}-${index}`}
                role="button"
                tabIndex={0}
                onClick={() => openAttachment(index)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openAttachment(index)
                  }
                }}
                className="tw:flex tw:flex-col tw:gap-2 tw:px-3 tw:py-2 tw:rounded tw:border tw:border-solid tw:border-md3-outline-variant tw:bg-md3-surface tw:cursor-pointer tw:outline-none hover:tw:shadow-md tw:focus-visible:ring-2 tw:focus-visible:ring-md3-primary"
              >
                <div className="tw:flex tw:items-start tw:gap-3">
                  <div className="tw:shrink-0 tw:mt-0.5">
                    <FileTypeIcon kind={kind} />
                  </div>
                  <div className="tw:flex-1 tw:min-w-0">
                    <div className="tw:text-body-md tw:font-medium tw:text-md3-on-surface tw:truncate">
                      {attachment.fileName}
                    </div>
                    <div className="tw:text-body-sm tw:text-md3-on-surface-variant">
                      {[attachment.size, typeText]
                        .filter(Boolean)
                        .join(' \u00b7 ')}
                    </div>
                  </div>
                  {!isReadOnly && !isDisabled && (
                    <button
                      type="button"
                      className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-full tw:border-none tw:bg-transparent tw:cursor-pointer tw:text-md3-on-surface-variant hover:tw:bg-md3-on-surface/8"
                      onClick={e => handleRemove(e, index)}
                    >
                      <CloseIcon size="xs" />
                    </button>
                  )}
                </div>

                {kind === 'image' && (
                  <img
                    src={attachment.url}
                    alt={attachment.fileName}
                    className="tw:max-h-[100px] tw:max-w-full tw:object-contain tw:object-left"
                  />
                )}
                {kind === 'video' && (
                  <video
                    src={attachment.url}
                    controls
                    onClick={e => e.stopPropagation()}
                    className="tw:max-h-[100px] tw:max-w-full tw:object-contain tw:object-left"
                  />
                )}
              </div>
            )
          })}
        </div>
      )}

      {!hideHelperText && displayError && (
        <div className="tw:text-body-sm tw:text-md3-error tw:px-2.5">
          {displayError}
        </div>
      )}
    </div>
  )
}
